import React from "react";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";

const sectionTitleSx = {
  fontWeight: 900,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.94)",
  mb: 0.45,
};

const sectionSubSx = {
  fontSize: "0.84rem",
  color: "rgba(255,255,255,0.5)",
  mb: 2,
  lineHeight: 1.6,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.03)",
    transition: "all 0.2s ease-in-out",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.05)",
      "& fieldset": { borderColor: "rgba(0,225,255,0.55)" },
    },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.58)" },
  "& .MuiInputBase-input": { color: "#fff" },
  "& input[type='date']::-webkit-calendar-picker-indicator, & input[type='time']::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
    cursor: "pointer",
    opacity: 0.72,
  },
};

const blockSx = {
  p: { xs: 1.4, md: 1.8 },
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.08)",
  bgcolor: "rgba(255,255,255,0.025)",
};

function SectionIntro({ icon, title, subtitle }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,225,255,0.12)",
            color: "#8ef4ff",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          <Typography sx={sectionSubSx}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export default function DataLocalStep({ state, actions, errors }) {
  const l = state.dataLocal.local;

  return (
    <Stack spacing={2}>
      <Box sx={blockSx}>
        <SectionIntro
          icon={<CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />}
          title="Quando vai rolar?"
          subtitle="Defina início, previsão de término e a informação de abertura dos portões."
        />

        <Grid container spacing={1.6}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Data início"
              InputLabelProps={{ shrink: true }}
              value={state.dataLocal.dataInicio}
              onChange={(e) => actions.set("dataLocal.dataInicio", e.target.value)}
              error={!!errors["dataLocal.dataInicio"]}
              helperText={errors["dataLocal.dataInicio"]}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="time"
              label="Hora início"
              InputLabelProps={{ shrink: true }}
              value={state.dataLocal.horaInicio}
              onChange={(e) => actions.set("dataLocal.horaInicio", e.target.value)}
              error={!!errors["dataLocal.horaInicio"]}
              helperText={errors["dataLocal.horaInicio"]}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Data fim (opcional)"
              InputLabelProps={{ shrink: true }}
              value={state.dataLocal.dataFim}
              onChange={(e) => actions.set("dataLocal.dataFim", e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="time"
              label="Hora fim (opcional)"
              InputLabelProps={{ shrink: true }}
              value={state.dataLocal.horaFim}
              onChange={(e) => actions.set("dataLocal.horaFim", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Abertura dos portões (opcional)"
              placeholder="Ex: 21h, ou '1 hora antes do show'"
              value={state.dataLocal.aberturaPortoes}
              onChange={(e) => actions.set("dataLocal.aberturaPortoes", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<PlaceRoundedIcon sx={{ fontSize: 20 }} />}
          title="Onde vai ser?"
          subtitle="Preencha o local completo para facilitar GPS, conferência e comunicação."
        />

        <Grid container spacing={1.6}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do local"
              placeholder="Ex: Espaço Hall, Arena XP..."
              value={l.nome}
              onChange={(e) => actions.set("dataLocal.local.nome", e.target.value)}
              error={!!errors["dataLocal.local.nome"]}
              helperText={errors["dataLocal.local.nome"]}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Endereço completo"
              placeholder="Rua, número, bairro..."
              value={l.endereco}
              onChange={(e) => actions.set("dataLocal.local.endereco", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Cidade"
              value={l.cidade}
              onChange={(e) => actions.set("dataLocal.local.cidade", e.target.value)}
              error={!!errors["dataLocal.local.cidade"]}
              helperText={errors["dataLocal.local.cidade"]}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="UF"
              placeholder="MG"
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
              value={l.estado}
              onChange={(e) => actions.set("dataLocal.local.estado", e.target.value.toUpperCase())}
              error={!!errors["dataLocal.local.estado"]}
              helperText={errors["dataLocal.local.estado"]}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Ponto de referência"
              placeholder="Ao lado do posto Ipiranga..."
              value={l.referencia}
              onChange={(e) => actions.set("dataLocal.local.referencia", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Link do Google Maps (opcional)"
              placeholder="https://goo.gl/maps/..."
              value={l.mapsUrl}
              onChange={(e) => actions.set("dataLocal.local.mapsUrl", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
            <Typography sx={{ mt: 0.8, fontSize: 12.5, color: "rgba(255,255,255,0.44)" }}>
              Dica: copie o link compartilhável direto do Google Maps.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}
